/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Link, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { billingAPI } from '../../api/crm'
import { useSubscription, useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import {
  COUNTRIES, CURRENCIES, FEATURE_LABELS, PHASES, countryName, daysUntil, formatBytes, formatMoney, formatNumber, formatStorage,
  invoiceFileName, plural, taxLabel,
} from '../../utils/billing'
import { downloadBlob } from '../../utils/helpers'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { useNotify } from '../../components/common/NotificationProvider'

const day = (iso) => format(new Date(iso), 'd MMM yyyy')

/** One sentence about where the subscription stands. */
const standing = (sub) => {
  const days = daysUntil(sub.phaseEndsAt)
  switch (sub.phase) {
    case 'TRIAL':
      return `Free trial until ${day(sub.phaseEndsAt)} (${plural(days, 'day')} left). Choose a plan to keep going after that.`
    case 'ACTIVE':
      if (sub.status === 'CANCELLED') return `Cancelled: everything works until ${day(sub.paidUntil)}, then the workspace becomes read-only. Choose a plan any time to keep going.`
      if (sub.status === 'PAST_DUE') return `The last renewal payment failed; ${sub.provider === 'MANUAL' ? 'please renew' : 'the payment will be retried'}. Paid until ${day(sub.paidUntil)}.`
      return sub.paidUntil
        ? `Paid${sub.interval ? ` ${sub.interval === 'YEAR' ? 'yearly' : 'monthly'}` : ''} until ${day(sub.paidUntil)}.`
        : 'No end date.'
    case 'GRACE':
      return `The renewal due on ${day(sub.paidUntil)} hasn't been paid. Everything works until ${day(sub.phaseEndsAt)}; after that the workspace becomes read-only.`
    case 'READ_ONLY':
      return `The subscription ended. People can view and export data but not change it. The workspace locks on ${day(sub.phaseEndsAt)}.`
    case 'LOCKED':
      return 'The subscription ended and the workspace is locked. Nothing has been deleted: renewing brings everything back at once.'
    default:
      return ''
  }
}

const UsageRow = ({ label, used, limit, format: fmt = formatNumber, usedText }) => {
  const percent = limit ? Math.min(100, (used / limit) * 100) : 0
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {usedText ?? formatNumber(used)} {limit == null ? '· no limit' : `of ${fmt(limit)}`}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={limit == null ? 0 : percent}
        color={percent >= 100 ? 'error' : percent >= 80 ? 'warning' : 'primary'} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  )
}

const PlanCard = ({ plan, current, interval, currency, actionLabel, onChoose }) => {
  const prices = plan.prices?.[currency] || {}
  const price = prices[interval]
  const seat = prices[interval === 'YEAR' ? 'SEAT_YEAR' : 'SEAT_MONTH']
  const per = interval === 'YEAR' ? '/ year' : '/ month'
  const lines = [
    `${plural(plan.includedMembers, 'member')} included`,
    `${formatNumber(plan.maxRecords)} records`,
    `${formatStorage(plan.storageMb)} file storage`,
    `${formatNumber(plan.maxAutomations)} automations`,
    `${formatNumber(plan.emailsPerDay)} emails a day`,
    ...plan.features.map((f) => FEATURE_LABELS[f] || f),
  ]
  return (
    <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 220px', minWidth: 0, borderColor: current ? 'primary.main' : undefined, borderWidth: current ? 2 : 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6">{plan.name}</Typography>
        {current && <Chip size="small" color="primary" label="Current" />}
      </Stack>
      {plan.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{plan.description}</Typography>}
      <Typography variant="h5" component="p">
        {price == null ? 'On request' : formatMoney(price, currency)}
        {price != null && <Typography component="span" variant="body2" color="text.secondary"> {per}</Typography>}
      </Typography>
      {seat != null && (
        <Typography variant="caption" color="text.secondary">Extra members: {formatMoney(seat, currency)} each {per}</Typography>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={0.75}>
        {lines.map((line) => (
          <Stack key={line} direction="row" spacing={1} alignItems="center">
            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2">{line}</Typography>
          </Stack>
        ))}
      </Stack>
      {onChoose && price != null && (
        <Button fullWidth variant={current ? 'outlined' : 'contained'} onClick={() => onChoose(plan)} sx={{ mt: 2 }}>{actionLabel}</Button>
      )}
    </Paper>
  )
}

/** Members needed beyond what the plan includes (members plus open invites must fit). */
const seatsNeeded = (plan, usage) => Math.max(0, (usage ? usage.members + usage.openInvites : 0) - plan.includedMembers)

/** Plan + interval + currency + extra members → total, then off to the payment provider's page. */
const CheckoutDialog = ({ plan, sub, interval, currency, provider, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const minimum = seatsNeeded(plan, sub.usage)
  const [seats, setSeats] = useState(String(Math.max(minimum, plan.code === sub.planCode ? sub.extraSeats : 0)))
  const [couponText, setCouponText] = useState('')
  const [coupon, setCoupon] = useState('')
  const prices = plan.prices?.[currency] || {}
  const seatPrice = prices[interval === 'YEAR' ? 'SEAT_YEAR' : 'SEAT_MONTH']
  const extra = Math.max(0, Number(seats) || 0)
  const total = (prices[interval] || 0) + (seatPrice != null ? extra * seatPrice : 0)
  const per = interval === 'YEAR' ? 'year' : 'month'
  const body = { planCode: plan.code, interval, currency, extraSeats: extra, couponCode: coupon || null }
  // how it would go: now, later (rest of the trial / credit for unused time), or changed in place
  const quote = useQuery({
    queryKey: [tenantId, 'billing', 'quote', body],
    queryFn: () => billingAPI.quote(body),
    enabled: extra >= minimum,
    retry: false,
  })
  const q = quote.data
  const start = useMutation({
    mutationFn: () => billingAPI.checkout(body),
    onSuccess: (checkout) => {
      if (checkout.redirectUrl) {
        // the provider's own page takes the payment; it sends people back here
        window.location.assign(checkout.redirectUrl)
        return
      }
      notify(`Changed to ${plan.name}`)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'billing'] })
      onClose()
    },
  })
  const replacing = sub.provider !== 'MANUAL' && sub.status !== 'CANCELLED' && sub.status !== 'TRIALING'
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); start.mutate() } }}>
      <DialogTitle>{plan.name}, billed {interval === 'YEAR' ? 'yearly' : 'monthly'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {(start.error || quote.error) && <Alert severity="error">{getErrorMessage(start.error || quote.error)}</Alert>}
          <Typography variant="body2">
            {plural(plan.includedMembers, 'member')} included{seatPrice != null && `; extra members ${formatMoney(seatPrice, currency)} each a ${per}`}.
          </Typography>
          {seatPrice != null && (
            <TextField type="number" label="Extra members" value={seats} onChange={(e) => setSeats(e.target.value)}
              inputProps={{ min: minimum, max: 1000 }} error={extra < minimum}
              helperText={minimum > 0 ? `At least ${minimum}: this workspace has ${sub.usage.members + sub.usage.openInvites} members and open invites` : ' '} />
          )}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField size="small" label="Coupon" value={couponText} onChange={(e) => setCouponText(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 40 }} sx={{ flex: 1 }} />
            {coupon ? (
              <Button onClick={() => { setCoupon(''); setCouponText('') }}>Remove</Button>
            ) : (
              <Button onClick={() => setCoupon(couponText.trim())} disabled={!couponText.trim()}>Apply</Button>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography>Price</Typography>
            <Typography variant="h5">{formatMoney(q ? q.amount : total, currency)} <Typography component="span" variant="body2" color="text.secondary">/ {per}</Typography></Typography>
          </Stack>
          {q?.taxAmount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5, textAlign: 'right' }}>
              {formatMoney(q.netAmount, currency)} + {formatMoney(q.taxAmount, currency)} {taxLabel(q.supply, q.taxRateBp)}
            </Typography>
          )}
          {q?.mode === 'CHANGE' && (
            <Alert severity="info" icon={false}>
              Changes right away. {q.provider} charges the difference for the rest of the current period to the saved payment method,
              and renewals are at the new price.
            </Alert>
          )}
          {q?.mode === 'LATER' && (
            <Alert severity="success" icon={false}>
              {q.reason === 'TRIAL'
                ? `Your trial continues${q.couponCode ? `, and coupon ${q.couponCode} adds free time worth ${formatMoney(q.couponValue, currency)}` : ''}: the first charge is on ${day(q.firstChargeAt)}.`
                : q.reason === 'COUPON'
                  ? `Coupon ${q.couponCode}: free time worth ${formatMoney(q.couponValue, currency)}. The plan starts now; the first charge is on ${day(q.firstChargeAt)}.`
                  : `Credit for the unused time on your current plan: ${formatMoney(q.credit, currency)}${q.couponCode ? `, plus coupon ${q.couponCode} worth ${formatMoney(q.couponValue, currency)}` : ''}. The new plan starts now; the first charge is on ${day(q.firstChargeAt)}.`}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Renews every {per} until you cancel.{q?.mode !== 'CHANGE' && replacing ? ' Your current subscription stops renewing once this is set up.' : ''}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={start.isPending || extra < minimum || !q}>
          {start.isPending ? (q?.mode === 'CHANGE' ? 'Changing…' : 'Opening…') : q?.mode === 'CHANGE' ? 'Change now' : `Continue to ${provider}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Back from the provider (?checkout=<id>): wait for its confirmation, which arrives by webhook. */
const CheckoutReturn = () => {
  const [params, setParams] = useSearchParams()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const id = params.get('checkout')
  const cancelled = params.get('cancelled') === '1'
  const [startedAt] = useState(() => Date.now())
  const checkout = useQuery({
    queryKey: [tenantId, 'billing', 'checkout', id],
    queryFn: () => billingAPI.getCheckout(id),
    enabled: !!id && !cancelled,
    // poll for two minutes; webhooks usually arrive within seconds
    refetchInterval: (q) => (q.state.data?.status === 'PENDING' && Date.now() - startedAt < 120_000 ? 2000 : false),
  })
  const status = checkout.data?.status
  useEffect(() => {
    if (status === 'COMPLETED') queryClient.invalidateQueries({ queryKey: [tenantId, 'billing', 'subscription'] })
  }, [status, queryClient, tenantId])
  if (!id) return null
  const dismiss = () => setParams({}, { replace: true })
  if (cancelled) return <Alert severity="info" onClose={dismiss} sx={{ mb: 2 }}>Payment cancelled; nothing was charged.</Alert>
  if (status === 'COMPLETED') return <Alert severity="success" onClose={dismiss} sx={{ mb: 2 }}>Payment confirmed. Thank you!</Alert>
  if (status === 'FAILED') return <Alert severity="error" onClose={dismiss} sx={{ mb: 2 }}>The payment didn&apos;t go through. Please try again.</Alert>
  if (checkout.error) return <Alert severity="error" onClose={dismiss} sx={{ mb: 2 }}>{getErrorMessage(checkout.error)}</Alert>
  return (
    <Alert severity="info" icon={<CircularProgress size={18} />} onClose={dismiss} sx={{ mb: 2 }}>
      Waiting for the payment provider to confirm your payment. This usually takes a few seconds; you can leave this page.
    </Alert>
  )
}

const InvoiceLink = ({ id, number }) => {
  const notify = useNotify()
  const download = async () => {
    try {
      downloadBlob(await billingAPI.invoicePdf(id), invoiceFileName(number))
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    }
  }
  return <Link component="button" type="button" onClick={download} sx={{ whiteSpace: 'nowrap' }}>{number}</Link>
}

/** Charges reported by the payment providers, with their GST invoices; credit notes for refunds. */
const Payments = () => {
  const tenantId = useTenantKey()
  const { data } = useQuery({ queryKey: [tenantId, 'billing', 'payments'], queryFn: billingAPI.payments, enabled: !!tenantId })
  const invoices = useQuery({ queryKey: [tenantId, 'billing', 'invoices'], queryFn: billingAPI.invoices, enabled: !!tenantId })
  const creditNotes = (invoices.data || []).filter((i) => i.kind === 'CREDIT_NOTE')
  if (!data?.length) return null
  return (
    <>
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Payments</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Period until</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((p) => (
              <TableRow key={p.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(p.createdOn)}</TableCell>
                <TableCell align="right">{formatMoney(p.amount, p.currency)}</TableCell>
                <TableCell><Chip size="small" color={p.status === 'PAID' ? 'success' : 'error'} label={p.status === 'PAID' ? 'Paid' : 'Failed'} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.periodEnd ? day(p.periodEnd) : '—'}</TableCell>
                <TableCell align="right">
                  {p.invoiceId ? <InvoiceLink id={p.invoiceId} number={p.invoiceNumber} />
                    : p.invoiceUrl && <Link href={p.invoiceUrl} target="_blank" rel="noopener noreferrer">Receipt</Link>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {creditNotes.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Credit notes</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Credit note</TableCell>
                  <TableCell>Against</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditNotes.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(c.invoiceDate)}</TableCell>
                    <TableCell><InvoiceLink id={c.id} number={c.number} /></TableCell>
                    <TableCell>{c.originalNumber}</TableCell>
                    <TableCell align="right">{formatMoney(c.total, c.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  )
}

const EMPTY_PROFILE = { legalName: '', addressLine1: '', addressLine2: '', city: '', postalCode: '', country: 'IN', stateCode: '', gstin: '', email: '' }

/** Who invoices are made out to. With GST invoices on, needed before paying (it decides the tax). */
const BillingDetails = () => {
  const tenantId = useTenantKey()
  const [editing, setEditing] = useState(false)
  const details = useQuery({ queryKey: [tenantId, 'billing', 'profile'], queryFn: billingAPI.profile, enabled: !!tenantId })
  if (!details.data) return null
  const { profile, invoicing, states } = details.data
  const state = profile?.stateCode && states.find((x) => x.code === profile.stateCode)
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Billing details</Typography>
        <Button size="small" onClick={() => setEditing(true)}>{profile ? 'Edit' : 'Add'}</Button>
      </Stack>
      {profile ? (
        <Typography variant="body2" component="div" color="text.secondary">
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{profile.legalName}</Box><br />
          {[profile.addressLine1, profile.addressLine2, `${profile.city}${profile.postalCode ? ` ${profile.postalCode}` : ''}`].filter(Boolean).join(', ')}
          {state ? `, ${state.name}` : ''}, {countryName(profile.country)}
          {profile.gstin && <><br />GSTIN {profile.gstin}</>}
          {profile.email && <><br />Invoices go to {profile.email}</>}
        </Typography>
      ) : invoicing ? (
        <Alert severity="info">Add your company name and address before choosing a plan: they go on your GST invoices. Add your GSTIN to claim input tax credit.</Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">The name and address your invoices are made out to.</Typography>
      )}
      {editing && <ProfileDialog details={details.data} onClose={() => setEditing(false)} />}
    </Paper>
  )
}

const ProfileDialog = ({ details, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [form, setForm] = useState(() => {
    const p = details.profile || {}
    return Object.fromEntries(Object.entries(EMPTY_PROFILE).map(([k, v]) => [k, p[k] ?? v]))
  })
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const india = form.country === 'IN'
  const save = useMutation({
    mutationFn: () => billingAPI.saveProfile({
      ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null])),
      stateCode: india ? form.stateCode || null : null,
      gstin: india ? form.gstin.trim().toUpperCase() || null : null,
    }),
    onSuccess: () => {
      notify('Billing details saved')
      queryClient.invalidateQueries({ queryKey: [tenantId, 'billing'] })
      onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); e.stopPropagation(); save.mutate() } }}>
      <DialogTitle>Billing details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          <TextField required label="Company or your name" value={form.legalName} onChange={set('legalName')} inputProps={{ maxLength: 200 }} />
          <TextField required label="Address" value={form.addressLine1} onChange={set('addressLine1')} inputProps={{ maxLength: 200 }} />
          <TextField label="Address line 2" value={form.addressLine2} onChange={set('addressLine2')} inputProps={{ maxLength: 200 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField required fullWidth label="City" value={form.city} onChange={set('city')} inputProps={{ maxLength: 100 }} />
            <TextField fullWidth label={india ? 'PIN code' : 'Postal code'} value={form.postalCode} onChange={set('postalCode')} inputProps={{ maxLength: 20 }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select required fullWidth label="Country" value={form.country} onChange={set('country')}>
              {COUNTRIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
            </TextField>
            {india && (
              <TextField select required fullWidth label="State" value={form.stateCode} onChange={set('stateCode')}
                helperText="Place of supply on your invoices">
                {details.states.map((x) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)}
              </TextField>
            )}
          </Stack>
          {india && (
            <TextField label="GSTIN (optional)" value={form.gstin} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
              inputProps={{ maxLength: 15 }} helperText="For a GST invoice you can claim input tax credit on, e.g. 27AAPFU0939F1ZV" />
          )}
          <TextField type="email" label="Send invoices to (optional)" value={form.email} onChange={set('email')} inputProps={{ maxLength: 320 }}
            helperText="Otherwise they're emailed to the workspace's admins" />
          <Typography variant="caption" color="text.secondary">
            Changes apply to future invoices. {details.invoicing && !details.pricesIncludeGst ? 'GST is added to prices for customers in India; there is none for customers abroad.' : ''}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={save.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Admins: the workspace's plan, what it uses, and the plans on offer. */
const BillingPage = () => {
  const tenantId = useTenantKey()
  const { data: sub, isLoading, error } = useSubscription()
  const plans = useQuery({ queryKey: [tenantId, 'billing', 'plans'], queryFn: billingAPI.plans, enabled: !!tenantId })
  const [intervalChoice, setIntervalChoice] = useState(null)
  const [currencyChoice, setCurrencyChoice] = useState(null)
  const [choosing, setChoosing] = useState(null)
  const [cancelling, setCancelling] = useState(false)
  const queryClient = useQueryClient()
  const cancel = useMutation({
    mutationFn: billingAPI.cancel,
    onSuccess: () => { setCancelling(false); queryClient.invalidateQueries({ queryKey: [tenantId, 'billing'] }) },
  })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>
  if (!sub.billingEnabled) return <Alert severity="info">This installation doesn&apos;t use subscriptions.</Alert>

  const shownInterval = intervalChoice || sub.interval || 'MONTH'
  const shownCurrency = currencyChoice || sub.currency || 'INR'
  const phase = PHASES[sub.phase]
  const usage = sub.usage
  const provider = sub.onlinePayments?.[shownCurrency]
  // the current plan, still renewing on the same terms: only the member count can change
  const renewing = sub.provider !== 'MANUAL' && ['ACTIVE', 'PAST_DUE'].includes(sub.status)
  const actionLabel = (plan) => (plan.code !== sub.planCode ? `Choose ${plan.name}`
    : renewing ? 'Change members or billing' : sub.phase === 'TRIAL' ? `Continue on ${plan.name}` : `Subscribe to ${plan.name}`)

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <PageHeader title="Billing" subtitle="Your plan, what this workspace uses, and the plans on offer." />
      <CheckoutReturn />

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Typography variant="h5">{sub.planName}</Typography>
          <Chip size="small" color={phase.color} label={phase.label} />
          {sub.extraSeats > 0 && <Chip size="small" variant="outlined" label={`+${plural(sub.extraSeats, 'extra seat')}`} />}
        </Stack>
        <Typography color="text.secondary" sx={{ mb: usage ? 3 : 0 }}>{standing(sub)}</Typography>
        {sub.cancellable && (
          <Button size="small" color="inherit" onClick={() => setCancelling(true)} sx={{ mt: -2, mb: 2 }}>Cancel subscription</Button>
        )}
        {usage && (
          <Stack spacing={2}>
            <UsageRow label="Members" used={usage.members + usage.openInvites} limit={sub.limits.seats}
              usedText={usage.openInvites ? `${usage.members} + ${usage.openInvites} invited` : formatNumber(usage.members)} />
            <UsageRow label="Records" used={usage.records} limit={sub.limits.records} />
            <UsageRow label="File storage" used={usage.storageBytes / 1024 / 1024} limit={sub.limits.storageMb}
              format={formatStorage} usedText={formatBytes(usage.storageBytes)} />
            <UsageRow label="Automations" used={usage.automations} limit={sub.limits.automations} />
            <UsageRow label="Emails in the last 24 hours" used={usage.emailsLast24h} limit={sub.limits.emailsPerDay} />
          </Stack>
        )}
      </Paper>

      {sub.canManage && <BillingDetails />}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Plans</Typography>
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup size="small" exclusive value={shownInterval} onChange={(_, v) => v && setIntervalChoice(v)}>
            <ToggleButton value="MONTH">Monthly</ToggleButton>
            <ToggleButton value="YEAR">Yearly</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={shownCurrency} onChange={(_, v) => v && setCurrencyChoice(v)}>
            {CURRENCIES.map((c) => <ToggleButton key={c} value={c}>{c}</ToggleButton>)}
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {provider ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payments in {shownCurrency} are handled securely by {provider}. Changing plan is fair: you only pay the difference, or get credit for unused time.
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Online payment in {shownCurrency} isn&apos;t available yet. Contact us to choose a plan, add members or renew, and we&apos;ll set it up for you.
        </Alert>
      )}
      {plans.isLoading ? <Loading /> : plans.error ? <Alert severity="error">{getErrorMessage(plans.error)}</Alert> : (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
          {plans.data.map((plan) => (
            <PlanCard key={plan.code} plan={plan} current={plan.code === sub.planCode} interval={shownInterval} currency={shownCurrency}
              actionLabel={actionLabel(plan)} onChoose={provider && plan.active ? setChoosing : null} />
          ))}
        </Stack>
      )}
      <Payments />
      {choosing && (
        <CheckoutDialog plan={choosing} sub={sub} interval={shownInterval} currency={shownCurrency} provider={provider} onClose={() => setChoosing(null)} />
      )}
      <ConfirmDialog
        open={cancelling}
        title="Cancel the subscription?"
        message={`It won't renew. Everything keeps working until ${sub.paidUntil ? day(sub.paidUntil) : 'the end of the paid period'}; after that the workspace becomes read-only. Nothing is deleted, and you can subscribe again any time.`}
        confirmLabel="Cancel subscription"
        destructive
        busy={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        onClose={() => setCancelling(false)}
      />
      {cancel.error && <Alert severity="error" sx={{ mt: 2 }}>{getErrorMessage(cancel.error)}</Alert>}
    </Box>
  )
}

export default BillingPage
